import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Download,
  Network,
  Beaker,
  Calendar,
  Users,
  TrendingUp,
  ExternalLink,
  Microscope,
  Activity,
  Heart
} from 'lucide-react';
import type { DatabaseState, Study, Compound, Mushroom, Condition } from '@/types/database';
import MoleculeViewer from './MoleculeViewer';

interface GraphNode {
  id: string;
  name: string;
  type: 'study' | 'compound' | 'mushroom' | 'condition';
  data?: any;
}

interface NodeDetailPanelProps {
  node: GraphNode | null;
  database: DatabaseState;
  onClose: () => void;
  onExploreConnections: (nodeId: string) => void;
}

export function NodeDetailPanel({
  node,
  database,
  onClose,
  onExploreConnections
}: NodeDetailPanelProps) {
  const [relatedData, setRelatedData] = useState<any>({});

  useEffect(() => {
    if (!node) return;

    // Gather related data based on node type
    const related: any = {
      compounds: [],
      studies: [],
      conditions: [],
      mushrooms: []
    };

    if (node.type === 'study') {
      // Get compounds for this study
      const studyCompounds = database.study_compounds?.filter(sc => sc.study_id === node.id) || [];
      related.compounds = studyCompounds.map(sc =>
        database.compounds?.find(c => c.compound_id === sc.compound_id)
      ).filter(Boolean);

      // Get conditions for this study
      const studyConditions = database.study_conditions?.filter(sc => sc.study_id === node.id) || [];
      related.conditions = studyConditions.map(sc =>
        database.conditions?.find(c => c.condition_id === sc.condition_id)
      ).filter(Boolean);

      // Get mushroom via intervention
      const intervention = database.interventions?.find(i => i.study_id === node.id);
      if (intervention) {
        related.mushrooms = [database.mushrooms?.find(m => m.mushroom_id === intervention.mushroom_id)].filter(Boolean);
      }

      // Get outcomes
      related.outcomes = database.outcomes?.filter(o => o.study_id === node.id) || [];

      // Get safety data
      related.safety = database.safety_data?.find(s => s.study_id === node.id);
    } else if (node.type === 'compound') {
      // Get studies containing this compound
      const compoundStudies = database.study_compounds?.filter(sc => sc.compound_id === node.id) || [];
      related.studies = compoundStudies.map(sc =>
        database.studies?.find(s => s.study_id === sc.study_id)
      ).filter(Boolean);
    } else if (node.type === 'mushroom') {
      // Get studies on this mushroom
      const mushroomInterventions = database.interventions?.filter(i => i.mushroom_id === node.id) || [];
      related.studies = mushroomInterventions.map(i =>
        database.studies?.find(s => s.study_id === i.study_id)
      ).filter(Boolean);

      // Get conditions studied with this mushroom
      const studyIds = new Set(mushroomInterventions.map(i => i.study_id));
      const conditionIds = new Set(
        database.study_conditions?.filter(sc => studyIds.has(sc.study_id)).map(sc => sc.condition_id) || []
      );
      related.conditions = Array.from(conditionIds).map(cid =>
        database.conditions?.find(c => c.condition_id === cid)
      ).filter(Boolean);
    } else if (node.type === 'condition') {
      // Get studies for this condition
      const conditionStudies = database.study_conditions?.filter(sc => sc.condition_id === node.id) || [];
      related.studies = conditionStudies.map(sc =>
        database.studies?.find(s => s.study_id === sc.study_id)
      ).filter(Boolean);

      // Get mushrooms used for this condition
      const studyIds = new Set(conditionStudies.map(cs => cs.study_id));
      const mushroomIds = new Set(
        database.interventions?.filter(i => studyIds.has(i.study_id)).map(i => i.mushroom_id) || []
      );
      related.mushrooms = Array.from(mushroomIds).map(mid =>
        database.mushrooms?.find(m => m.mushroom_id === mid)
      ).filter(Boolean);
    }

    setRelatedData(related);
  }, [node, database]);

  const handleExportData = () => {
    if (!node) return;

    const exportData = {
      node: node.data,
      related: relatedData,
      exportDate: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${node.type}-${node.name.replace(/[^a-z0-9]/gi, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!node) return null;

  return (
    <Sheet open={!!node} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            {node.type === 'study' && <Microscope className="h-6 w-6 text-[#A98A2C]" />}
            {node.type === 'compound' && <Beaker className="h-6 w-6 text-[#C26A1E]" />}
            {node.type === 'mushroom' && <Activity className="h-6 w-6 text-[#B98B36]" />}
            {node.type === 'condition' && <Heart className="h-6 w-6 text-[#B45F3D]" />}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-xl">{node.name}</SheetTitle>
                <Badge variant="outline">{node.type}</Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        <Separator className="my-4" />

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-6 pr-4">
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => onExploreConnections(node.id)}
                variant="default"
                className="flex-1"
              >
                <Network className="mr-2 h-4 w-4" />
                Explore Connections
              </Button>
              <Button onClick={handleExportData} variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export Data
              </Button>
            </div>

            {/* Study Details */}
            {node.type === 'study' && (
              <StudyDetails study={node.data} relatedData={relatedData} />
            )}

            {/* Compound Details */}
            {node.type === 'compound' && (
              <CompoundDetails compound={node.data} relatedData={relatedData} />
            )}

            {/* Mushroom Details */}
            {node.type === 'mushroom' && (
              <MushroomDetails mushroom={node.data} relatedData={relatedData} />
            )}

            {/* Condition Details */}
            {node.type === 'condition' && (
              <ConditionDetails condition={node.data} relatedData={relatedData} />
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function StudyDetails({ study, relatedData }: { study: Study; relatedData: any }) {
  return (
    <div className="space-y-4">
      {/* Study Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Study Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Authors:</span>
            <span className="text-muted-foreground">{study.authors}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Year:</span>
            <span className="text-muted-foreground">{study.year}</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Quality Score:</span>
            <Badge variant="secondary">{study.quality_score}/10</Badge>
          </div>
          <div>
            <span className="font-medium">Journal:</span>
            <p className="text-muted-foreground mt-1">{study.journal}</p>
          </div>
          {study.doi && (
            <a
              href={`https://doi.org/${study.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[#A98A2C] hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View DOI
            </a>
          )}
        </CardContent>
      </Card>

      {/* Key Findings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Key Findings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {study.key_finding_summary || study.abstract?.substring(0, 400) + '...'}
          </p>
        </CardContent>
      </Card>

      {/* Compounds Used */}
      {relatedData.compounds?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Beaker className="h-4 w-4" />
              Compounds ({relatedData.compounds.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {relatedData.compounds.map((compound: Compound) => (
                <Badge key={compound.compound_id} variant="secondary">
                  {compound.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conditions Treated */}
      {relatedData.conditions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Conditions Studied ({relatedData.conditions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {relatedData.conditions.map((condition: Condition) => (
                <Badge key={condition.condition_id} variant="outline">
                  {condition.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mushroom Species */}
      {relatedData.mushrooms?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Mushroom Species
            </CardTitle>
          </CardHeader>
          <CardContent>
            {relatedData.mushrooms.map((mushroom: Mushroom) => (
              <div key={mushroom.mushroom_id} className="text-sm">
                <p className="font-medium">{mushroom.common_name}</p>
                <p className="text-muted-foreground italic">{mushroom.scientific_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Outcomes */}
      {relatedData.outcomes?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Outcomes ({relatedData.outcomes.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {relatedData.outcomes.slice(0, 5).map((outcome: any, idx: number) => (
              <div key={idx} className="text-sm border-l-2 border-[#C8A53D] pl-3">
                <p className="font-medium">{outcome.measure}</p>
                <p className="text-muted-foreground">{outcome.result}</p>
                {outcome.statistical_significance && (
                  <Badge variant="secondary" className="mt-1">
                    Statistically Significant
                  </Badge>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Safety */}
      {relatedData.safety && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Safety Profile</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div>
              <span className="font-medium">Adverse Events:</span>
              <p className="text-muted-foreground">{relatedData.safety.adverse_events || 'None reported'}</p>
            </div>
            {relatedData.safety.safety_notes && (
              <div>
                <span className="font-medium">Notes:</span>
                <p className="text-muted-foreground">{relatedData.safety.safety_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CompoundDetails({ compound, relatedData }: { compound: Compound; relatedData: any }) {
  return (
    <div className="space-y-4">
      {/* Compound Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compound Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Chemical Name:</span>
            <p className="text-muted-foreground mt-1">{compound.chemical_name}</p>
          </div>
          <div>
            <span className="font-medium">Category:</span>
            <Badge variant="secondary" className="ml-2">{compound.category}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Molecular Structure */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Molecular Structure</CardTitle>
        </CardHeader>
        <CardContent>
          <MoleculeViewer compoundName={compound.name} />
        </CardContent>
      </Card>

      {/* Related Studies */}
      {relatedData.studies?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Related Studies ({relatedData.studies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {relatedData.studies.slice(0, 5).map((study: Study) => (
              <div key={study.study_id} className="text-sm border-l-2 border-[#B98B36] pl-3">
                <p className="font-medium">{study.title}</p>
                <p className="text-muted-foreground text-xs">
                  {study.authors.split(',')[0]} et al., {study.year}
                </p>
              </div>
            ))}
            {relatedData.studies.length > 5 && (
              <p className="text-xs text-muted-foreground">
                +{relatedData.studies.length - 5} more studies
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Research Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Research Statistics</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-[#C26A1E]">{relatedData.studies?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Total Studies</p>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-[#C26A1E]">
              {relatedData.studies?.filter((s: Study) => s.study_type === 'RCT').length || 0}
            </p>
            <p className="text-xs text-muted-foreground">RCTs</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MushroomDetails({ mushroom, relatedData }: { mushroom: Mushroom; relatedData: any }) {
  return (
    <div className="space-y-4">
      {/* Mushroom Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Species Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Common Name:</span>
            <p className="text-lg mt-1">{mushroom.common_name}</p>
          </div>
          <div>
            <span className="font-medium">Scientific Name:</span>
            <p className="text-muted-foreground italic">{mushroom.scientific_name}</p>
          </div>
          {mushroom.aliases?.length > 0 && (
            <div>
              <span className="font-medium">Also Known As:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {mushroom.aliases.map((alias, idx) => (
                  <Badge key={idx} variant="outline">{alias}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Research Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Research Overview</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-[#B98B36]">{mushroom.total_studies}</p>
            <p className="text-sm text-muted-foreground mt-1">Total Studies</p>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-[#B98B36]">{relatedData.conditions?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Conditions</p>
          </div>
        </CardContent>
      </Card>

      {/* Top Conditions Studied */}
      {relatedData.conditions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Conditions Studied ({relatedData.conditions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {relatedData.conditions.map((condition: Condition) => (
                <div key={condition.condition_id} className="flex items-center justify-between text-sm">
                  <span>{condition.name}</span>
                  <Badge variant="secondary">{condition.category}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Studies */}
      {relatedData.studies?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Recent Studies ({relatedData.studies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {relatedData.studies
              .sort((a: Study, b: Study) => b.year - a.year)
              .slice(0, 5)
              .map((study: Study) => (
                <div key={study.study_id} className="text-sm border-l-2 border-[#C26A1E] pl-3">
                  <p className="font-medium">{study.title}</p>
                  <p className="text-muted-foreground text-xs">
                    {study.year} • Quality: {study.quality_score}/10
                  </p>
                </div>
              ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ConditionDetails({ condition, relatedData }: { condition: Condition; relatedData: any }) {
  return (
    <div className="space-y-4">
      {/* Condition Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Condition Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Condition:</span>
            <p className="text-lg mt-1">{condition.name}</p>
          </div>
          <div>
            <span className="font-medium">Category:</span>
            <Badge variant="secondary" className="ml-2">{condition.category}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Research Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Research Overview</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-[#B45F3D]">{relatedData.studies?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Total Studies</p>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-[#B45F3D]">{relatedData.mushrooms?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Mushroom Species</p>
          </div>
        </CardContent>
      </Card>

      {/* Most Studied Species */}
      {relatedData.mushrooms?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Mushroom Species Studied ({relatedData.mushrooms.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {relatedData.mushrooms.map((mushroom: Mushroom) => (
                <div key={mushroom.mushroom_id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{mushroom.common_name}</p>
                    <p className="text-xs text-muted-foreground italic">{mushroom.scientific_name}</p>
                  </div>
                  <Badge variant="outline">{mushroom.total_studies} studies</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Related Studies */}
      {relatedData.studies?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Clinical Studies ({relatedData.studies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {relatedData.studies
              .sort((a: Study, b: Study) => b.year - a.year)
              .slice(0, 5)
              .map((study: Study) => (
                <div key={study.study_id} className="text-sm border-l-2 border-[#C8A53D] pl-3">
                  <p className="font-medium">{study.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{study.study_type}</Badge>
                    <span className="text-xs text-muted-foreground">{study.year}</span>
                  </div>
                </div>
              ))}
            {relatedData.studies.length > 5 && (
              <p className="text-xs text-muted-foreground">
                +{relatedData.studies.length - 5} more studies
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
